/**
 * Menu Extraction Thunks
 * Redux async actions for AI menu extraction
 */

import { createAsyncThunk } from '@reduxjs/toolkit';
import {
  commitMenuExtraction as commitMenuExtractionService,
  createMenuExtraction as createMenuExtractionService,
  declineAndRerunExtraction as declineAndRerunExtractionService,
  extractMenuWithAI,
  fetchAllExtractions,
  fetchPendingExtractions as fetchPendingExtractionsService,
  updateMenuExtraction as updateMenuExtractionService,
} from '../../services/menuExtractionService';
import {
  CommitMenuExtractionRequest,
  CreateMenuExtractionRequest,
  MenuExtractionOutput,
  UpdateMenuExtractionRequest,
} from '../../types/menu';

/**
 * Fetch pending extractions for the current outlet
 */
export const fetchPendingExtractions = createAsyncThunk(
  'menuExtractions/fetchPending',
  async (outletId: string, { rejectWithValue }) => {
    try {
      const extractions = await fetchPendingExtractionsService(outletId);
      return extractions;
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : 'Failed to fetch extractions',
      );
    }
  },
);

/**
 * Fetch all extractions for an outlet
 */
export const fetchExtractions = createAsyncThunk(
  'menuExtractions/fetchAll',
  async (outletId: string, { rejectWithValue }) => {
    try {
      const extractions = await fetchAllExtractions(outletId);
      return extractions;
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : 'Failed to fetch extractions',
      );
    }
  },
);

/**
 * Create a new menu extraction from uploaded file text
 */
export const createMenuExtraction = createAsyncThunk(
  'menuExtractions/create',
  async (request: CreateMenuExtractionRequest, { rejectWithValue }) => {
    try {
      const extraction = await createMenuExtractionService(request);
      return extraction;
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : 'Failed to create extraction',
      );
    }
  },
);

/**
 * Process extraction with AI
 */
export const processExtractionWithAI = createAsyncThunk(
  'menuExtractions/processWithAI',
  async (
    payload: {
      extractionId: string;
      rawText: string;
      departmentNames: string[];
    },
    { rejectWithValue },
  ) => {
    try {
      const result = await extractMenuWithAI(
        payload.extractionId,
        payload.rawText,
        payload.departmentNames,
      );
      return { extractionId: payload.extractionId, output: result };
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : 'AI extraction failed',
      );
    }
  },
);

/**
 * Update extraction (status or output)
 */
export const updateMenuExtraction = createAsyncThunk(
  'menuExtractions/update',
  async (request: UpdateMenuExtractionRequest, { rejectWithValue }) => {
    try {
      const extraction = await updateMenuExtractionService(request);
      return extraction;
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : 'Failed to update extraction',
      );
    }
  },
);

/**
 * Commit extraction to live menu
 */
export const commitExtraction = createAsyncThunk(
  'menuExtractions/commit',
  async (request: CommitMenuExtractionRequest, { rejectWithValue }) => {
    try {
      const result = await commitMenuExtractionService(request);
      return { ...result, extractionId: request.extraction_id };
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : 'Failed to commit extraction',
      );
    }
  },
);

/**
 * Decline current extraction and create a new version for re-extraction
 */
export const declineAndRerunExtraction = createAsyncThunk(
  'menuExtractions/declineAndRerun',
  async (
    payload: { extractionId: string; outletId: string },
    { rejectWithValue },
  ) => {
    try {
      const newExtraction = await declineAndRerunExtractionService(
        payload.extractionId,
        payload.outletId,
      );
      return newExtraction;
    } catch (error) {
      return rejectWithValue(
        error instanceof Error
          ? error.message
          : 'Failed to decline and rerun extraction',
      );
    }
  },
);

/**
 * Save manual edits to extraction output
 */
export const saveExtractionEdits = createAsyncThunk(
  'menuExtractions/saveEdits',
  async (
    payload: { extractionId: string; output: MenuExtractionOutput },
    { rejectWithValue },
  ) => {
    try {
      const extraction = await updateMenuExtractionService({
        id: payload.extractionId,
        json_output: payload.output,
        status: 'reviewed',
      });
      return extraction;
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : 'Failed to save edits',
      );
    }
  },
);
